<template>
  <div v-if="data?.cards && data?.cards.length > 1" class="relative">
    <ClientOnly>
      <div ref="root" class="testimonial-carousel swiper dr-carousel dr-carousel--centered">
        <div
          class="dr-carousel-track"
          data-carousel-track
          :style="height ? { height: `${height}px` } : undefined"
        >
          <div
            v-for="({ item, clone, key }, slide) in slides"
            :key="data.id + key"
            class="dr-carousel-slide"
            data-carousel-slide
            :inert="clone || undefined"
            :aria-hidden="clone ? 'true' : undefined"
          >
            <div
              class="flex justify-center py-12 lg:flex-row lg:py-9"
              :class="[
                slide === index - 1 &&
                  'translate-x-20 opacity-40 sm:translate-x-36 lg:translate-x-0',
                slide === index + 1 &&
                  '-translate-x-20 opacity-40 sm:-translate-x-24 lg:translate-x-0',
                item.image?.url ? 'flex-col-reverse' : 'flex-col',
              ]"
            >
              <div
                class="flex flex-col items-center pt-8 lg:pt-0"
                :class="[!item.image?.url ? 'hidden' : 'flex']"
              >
                <NuxtImg
                  v-if="item?.image?.url"
                  :src="item.image?.url"
                  :alt="item.image?.alt || ''"
                  :provider="item.provider || 'cloudinary'"
                  width="300"
                  height="300"
                  class="aspect-square !w-[100px] rounded-full lg:!w-[300px]"
                  loading="lazy"
                  sizes="xs:100px sm:150px md:200px lg:250px xl:300px"
                  fit="fill"
                />
                <!-- Mobile: Name and position directly under image -->
                <div class="mt-4 flex flex-col items-center lg:hidden">
                  <div class="font-sans text-lg text-blue">{{ item.name }}</div>
                  <div class="mt-2 flex flex-col space-y-1">
                    <div v-if="item?.position" class="text-center font-sans">
                      {{ item.position }}
                    </div>
                    <div v-if="item?.date" class="font-sans">
                      {{ item.date }}
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="mx-14 flex max-w-[600px] flex-col items-center space-y-3 self-center py-2 lg:ml-[84px] lg:mr-12 lg:items-start lg:self-start lg:py-4"
              >
                <Quotes class="h-4 lg:h-auto lg:-translate-x-14" />
                <h3 class="font-serif text-lg font-bold leading-normal lg:block lg:text-3xl">
                  {{ item.title }}
                </h3>
                <div
                  class="space-y-4 text-center font-sans text-sm lg:text-left lg:text-lg"
                  v-html="item.copy"
                ></div>

                <Quotes class="mt-2 h-4 lg:mt-4 lg:h-auto lg:translate-x-10 lg:self-end" />

                <!-- Desktop: Name and position at bottom right -->
                <div
                  class="mt-4 hidden w-full flex-col items-center lg:mt-6 lg:flex lg:items-end lg:text-right"
                >
                  <div class="font-sans text-blue lg:text-2xl">{{ item.name }}</div>
                  <div class="mt-2 flex flex-col space-y-1 lg:space-y-2">
                    <div v-if="item?.position" class="font-sans lg:text-xl">
                      {{ item.position }}
                    </div>
                    <div v-if="item?.date" class="font-sans lg:text-xl">
                      {{ item.date }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <CarouselDots
          dynamic
          :count="data.cards.length"
          :active="index % data.cards.length"
          @select="card => slideTo(first + card)"
        />
      </div>
      <SliderNavigation :slider="navigation" />
    </ClientOnly>
  </div>
  <div v-else-if="data?.cards" class="relative">
    <div
      class="flex justify-center py-12 lg:flex-row lg:py-9"
      :class="[data?.cards[0]?.image?.url ? 'flex-col-reverse' : 'flex-col']"
    >
      <div
        class="flex flex-col items-center pt-8 lg:pt-0"
        :class="[!data?.cards[0]?.image?.url ? 'hidden' : 'flex']"
      >
        <NuxtImg
          v-if="data?.cards?.[0]?.image?.url"
          :src="data?.cards[0]?.image?.url"
          :alt="data?.cards[0]?.image?.alt || ''"
          :provider="data?.cards[0]?.provider || 'cloudinary'"
          width="300"
          height="300"
          class="aspect-square !w-[100px] rounded-full lg:!w-[300px]"
          loading="lazy"
          sizes="xs:100px sm:150px md:200px lg:250px xl:300px"
          fit="fill"
        />
        <!-- Mobile: Name and position directly under image -->
        <div class="mt-4 flex flex-col items-center lg:hidden">
          <div class="font-sans text-lg text-blue">{{ data?.cards[0]?.name }}</div>
          <div class="mt-2 flex flex-col space-y-1">
            <div v-if="data?.cards[0]?.position" class="font-sans text-sm">
              {{ data?.cards[0]?.position }}
            </div>
            <div v-if="data?.cards[0]?.date" class="font-sans text-sm">
              {{ data?.cards[0]?.date }}
            </div>
          </div>
        </div>
      </div>
      <div
        class="mx-14 flex max-w-[600px] flex-col items-center space-y-3 self-center py-2 lg:ml-[84px] lg:mr-12 lg:items-start lg:self-start lg:py-4"
      >
        <Quotes class="h-4 lg:h-auto lg:-translate-x-14" />
        <h3
          :class="[
            'font-serif text-lg font-bold leading-normal lg:block lg:text-3xl',
            useDarkMode ? 'text-white' : '',
          ]"
        >
          {{ data?.cards[0]?.title }}
        </h3>
        <div
          :class="[
            'space-y-4 text-center font-sans text-sm lg:text-left lg:text-lg',
            useDarkMode ? 'text-white' : '',
          ]"
          v-html="data?.cards[0]?.copy"
        ></div>

        <Quotes class="mt-2 h-4 lg:mt-4 lg:h-auto lg:translate-x-10 lg:self-end" />

        <!-- Desktop: Name and position at bottom right -->
        <div
          class="mt-4 hidden w-full flex-col items-center lg:mt-6 lg:flex lg:items-end lg:text-right"
        >
          <div class="font-sans text-blue lg:text-2xl">{{ data?.cards[0]?.name }}</div>
          <div class="mt-2 flex flex-col space-y-1 lg:space-y-2">
            <div
              v-if="data?.cards[0]?.position"
              :class="['font-sans lg:text-xl', useDarkMode ? 'text-white' : '']"
            >
              {{ data?.cards[0]?.position }}
            </div>
            <div
              v-if="data?.cards[0]?.date"
              :class="['font-sans lg:text-xl', useDarkMode ? 'text-white' : '']"
            >
              {{ data?.cards[0]?.date }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// Sibling: packages/astro-ui/src/components/DynamicBlocks/TestimonialBlock.astro
import '../../assets/styles/carousel.css'
import { ref, computed, watch, inject, onBeforeUnmount } from 'vue'
import type { Ref } from 'vue'

import Quotes from '../atoms/Icons/Quotes.vue'
import SliderNavigation from '../atoms/SliderNavigation.vue'
import CarouselDots from '../atoms/CarouselDots.vue'
import NuxtImg from '../atoms/NuxtImg.vue'
import { useCarousel } from '../../composables/useCarousel'
import { loopCopies } from '../../utils/carousel'

import type { ICardData } from '../../types'

interface ITestimonialBlockCard extends ICardData {
  date?: string
  position?: string
  name?: string
  provider?: string
}

export interface ITestimonialBlock {
  id: string
  cards?: ITestimonialBlockCard[]
}

interface Props {
  data: ITestimonialBlock
}

const props = defineProps<Props>()

const settings = inject<Ref<string[]>>('settings', ref([]))
const useDarkMode = computed(() => settings.value?.includes('dark_theme') ?? false)

// Swiper looped only from three cards up; the copies either side make the loop.
const count = props.data.cards?.length ?? 0
const loop = count > 2
const slides = loopCopies(props.data.cards ?? [], loop)
const first = loop ? count : 0

const root = ref<HTMLElement>()
const { index, next, prev, slideTo } = useCarousel(root, {
  index: first,
  loopPeriod: loop ? count : undefined,
})
// SliderNavigation drives a Swiper elsewhere; these are the two calls it makes.
const navigation = { slideNext: next, slidePrev: prev }

// Swiper's autoHeight: the strip is as tall as the active card, also when its image loads.
const height = ref<number>()
let resizeObserver: ResizeObserver | undefined
watch(
  [root, index],
  ([el, active]) => {
    resizeObserver?.disconnect()
    const slide = el?.querySelectorAll<HTMLElement>('[data-carousel-slide]')[active]
    if (!slide || typeof ResizeObserver === 'undefined') return
    resizeObserver = new ResizeObserver(() => (height.value = slide.offsetHeight))
    resizeObserver.observe(slide)
    height.value = slide.offsetHeight
  },
  { flush: 'post' },
)
onBeforeUnmount(() => resizeObserver?.disconnect())
</script>

<style>
/* Room for the arrows and dots. It used to leak in from other sliders' global `.swiper` rules,
   so it hung on load order; flashapp's /login got 70px. */
.testimonial-carousel.swiper {
  padding-bottom: 70px;
}
/* Swiper's .swiper-autoheight. */
.testimonial-carousel > .dr-carousel-track {
  align-items: flex-start;
  transition-property: transform, height;
}
.testimonial-carousel .dr-carousel-slide {
  height: auto;
}
/* The previous card peeks in over the active one on narrow screens. */
.testimonial-carousel .dr-carousel-slide.is-prev {
  z-index: 1000;
}
</style>
